import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import MetaHead from "../components/shared/MetaHead";

import BackgroundImagePanel from "../components/shared/BackgroundImagePanel";
import ChildrenNavigation from "../components/shared/layouts/ChildrenNavigation";
import PageContent from "../components/shared/layouts/PageContent";
import PageNavigation from "../components/shared/layouts/PageNavigation";

import { gql, useQuery } from "@apollo/client";
import client from "../lib/apollo";
import { Loader } from "@googlemaps/js-api-loader";
import usePage from "../contexts/PageContext";
import {
  ADD_LOCATIONS_WITH_CONTENT,
  ADD_PAGES,
  ADD_PAGES_PARENT_CHILDREN,
  ADD_PAGES_PARENT_CHILDREN2,
} from "../lib/graphql";

export default function SideNavPage(props) {
  const { siteData, setSiteData } = usePage();
  const location = useLocation();

  const curLocation = location.pathname.replace("/", "");
  const curLocations = location.pathname.includes("/")
    ? location.pathname.split("/").filter((item) => item !== "")
    : location.pathname;
  const slugId = curLocations[curLocations.length - 1];
  const parentSlugId = curLocations[0].replace("/", "") || null; // watch out for any with 3 segments
  const curParent =
    curLocations.length > 1 ? siteData.pages[parentSlugId] || null : null;

  let GET_PAGE_DATA;

  if (curParent && curLocations.length < 2) {
    let curQueries = !siteData?.locations?.length
      ? ADD_PAGES + ADD_LOCATIONS_WITH_CONTENT
      : ADD_PAGES;

    GET_PAGE_DATA = gql`
  query getPageData($id: String!) {    
    ${curQueries}
  }
`;
  } else if (!curParent && !parentSlugId) {
    let curQueries = !siteData?.locations?.length
      ? ADD_PAGES_PARENT_CHILDREN + ADD_LOCATIONS_WITH_CONTENT
      : ADD_PAGES_PARENT_CHILDREN;

    GET_PAGE_DATA = gql`
    query getPageData($id: String!) {    
      ${curQueries}
    }
  `;
  } else {
    let curQueries = !siteData?.locations?.length
      ? ADD_PAGES_PARENT_CHILDREN2 + ADD_LOCATIONS_WITH_CONTENT
      : ADD_PAGES_PARENT_CHILDREN2;

    GET_PAGE_DATA = gql`
    query getPageData($id: String!, $parent: ID!) {    
      ${curQueries}
    }
  `;
  }

  const isValidCoord = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "string" && v.trim() === "") return false;
    const n = parseFloat(v);
    return Number.isFinite(n);
  };

  // Only skip the query if we have a cached page with valid coordinates.
  const cached = siteData?.pages?.[curLocation];
  const shouldSkip = cached && isValidCoord(cached?.googleMaps?.latitude) && isValidCoord(cached?.googleMaps?.longitude);
  try {
    // diagnostic removed
  } catch (e) {}

  const { data: pageData } = useQuery(GET_PAGE_DATA, {
    variables: { id: slugId, parent: parentSlugId },
    skip: shouldSkip,
  });

  useEffect(() => {
    if (pageData && pageData.pages) {
      if (!siteData.locations.length && pageData?.locations?.length) {
        siteData.locations = pageData.locations;
      }
      let newSiteData = { ...siteData };
      let newPage;

      if (pageData.pages?.nodes?.length > 1 && parentSlugId) {
        newPage = pageData.pages.nodes.filter(
          (page) => page?.parent?.node?.slug === parentSlugId
        )[0];
      } else {
        newPage = pageData.pages?.nodes[0] || {};
      }

      const featuredImage = newPage?.featuredImage?.node || null;
      const hasParent = curParent || newPage.parent?.node || null;
  // prepare newSiteData
      if (hasParent && !siteData?.pages[parentSlugId]) {
        newSiteData.pages[parentSlugId] = hasParent;
      }
      let curChildren;

      if (hasParent) {
        curChildren = hasParent?.children?.nodes || [];
        const parentFeaturedImage = hasParent?.featuredImage?.node || null;

        if (parentFeaturedImage?.mediaItemUrl)
          newSiteData.curPanelBackground = parentFeaturedImage.mediaItemUrl;

        // be careful here some childs have images in locations
        newSiteData.pages = {
          ...siteData.pages,
          [curLocation]: {
            ...hasParent,
            children: curChildren,
            featuredImage: parentFeaturedImage,
          },
        };
      } else {
        curChildren = newPage.children?.edges || [];
        if (featuredImage?.mediaItemUrl)
          newSiteData.curPanelBackground = featuredImage.mediaItemUrl;
      }

      let newChildren = {};
      if (curChildren && curChildren.length > 0) {
        curChildren.forEach((child) => {
          const node = child?.node || child;
          const g = node?.googleMaps;
          const lat = g ? parseFloat(g.latitude) : NaN;
          const lng = g ? parseFloat(g.longitude) : NaN;
          const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
          newChildren[`${parentSlugId}/${node?.slug || node}`] = {
            ...node,
            maybeHasMap: hasCoords,
          };
        });
      }
      if (featuredImage?.mediaItemUrl)
        newSiteData.curPanelBackground = featuredImage.mediaItemUrl;

      if (siteData.curChildren !== curChildren) {
        newSiteData.curChildren = curChildren?.nodes || curChildren;
      }

      const newPageData = {
        ...newPage,
        content: newPage.content.replace(
          '<img decoding="async"',
          '<img decoding="async" loading="lazy"'
        ),
        children: curChildren,
        featuredImage: featuredImage,
      };

      try {
        // diagnostic removed
      } catch (e) {}

      newSiteData.pages = {
        ...siteData.pages,
        ...newChildren,
        [curLocation]: newPageData,
      };
      newSiteData.curParent = hasParent;

      setSiteData({
        ...newSiteData,
        prevLocation: newSiteData.curLocation,
        curLocation: curLocation,
        curLocations: curLocations,
        locations: pageData?.locations?.edges || siteData.locations || [],
        curSlug: slugId,
        isLoading: false,
      });

      // Background-prefetch child page coordinates when preparing a
      // parent's children list. This fills missing `googleMaps` on cached
      // child entries so navigation from parent -> child can reserve the
      // map placeholder immediately.
      try {
        Object.keys(newChildren || {}).forEach((childKey) => {
          const childEntry = newSiteData.pages?.[childKey];
          const hasCoords = childEntry && isValidCoord(childEntry?.googleMaps?.latitude) && isValidCoord(childEntry?.googleMaps?.longitude);
          if (!hasCoords) {
            const childSlug = (childEntry && childEntry.slug) || childKey.split("/").pop();
            try {
              // background prefetch child coords
            } catch (e) {}
            client
              .query({
                query: gql`
                  query getChild($id: String!) {    
                    ${ADD_PAGES}
                  }
                `,
                variables: { id: childSlug },
              })
              .then((res) => {
                const fetched = res?.data?.pages?.nodes?.[0];
                try {
                  // background prefetch result
                } catch (e) {}
                if (fetched) {
                  setSiteData((prev) => ({
                    ...prev,
                    pages: {
                      ...prev.pages,
                      [childKey]: {
                        ...prev.pages[childKey],
                        ...fetched,
                        maybeHasMap: fetched?.googleMaps && isValidCoord(fetched.googleMaps.latitude) && isValidCoord(fetched.googleMaps.longitude),
                      },
                    },
                  }));
                }
              })
              .catch(() => {});
          }
        });
      } catch (e) {}

      // If the newly cached current page lacks googleMaps coords, try fetching
      // this page directly to populate latitude/longitude so maps can mount
      // immediately after navigation. Log the decision for diagnostics.
      try {
        const cachedCur = newSiteData.pages?.[curLocation];
        if (
          cachedCur &&
          !isValidCoord(cachedCur?.googleMaps?.latitude) &&
          !isValidCoord(cachedCur?.googleMaps?.longitude)
        ) {
          try {
            // cachedCur missing coords - fallback fetch
          } catch (e) {}
          client
            .query({
              query: gql`
                query getPageDirect($id: String!) {    
                  ${ADD_PAGES}
                }
              `,
              variables: { id: slugId },
            })
            .then((res) => {
              const fetched = res?.data?.pages?.nodes?.[0];
              try {
                // fallback fetch returned
              } catch (e) {}
              if (fetched) {
                setSiteData((prev) => ({
                  ...prev,
                  pages: {
                    ...prev.pages,
                    [curLocation]: {
                      ...prev.pages[curLocation],
                      ...fetched,
                    },
                  },
                }));
              }
            })
            .catch(() => {});
        }
      } catch (e) {}

      // If any of the prepared pages (current or children) include valid
      // googleMaps coordinates, warm up the Google Maps JS API to reduce
      // the perceived load-time on first mount.
      try {
        const anyWithCoords = (Object.values(newSiteData.pages || {}) || []).some((p) => {
          const g = p?.googleMaps;
          if (!g) return false;
          const lat = parseFloat(g.latitude);
          const lng = parseFloat(g.longitude);
          return Number.isFinite(lat) && Number.isFinite(lng);
        });

        if (anyWithCoords) {
          const loader = new Loader({ apiKey: "AIzaSyBN8jwUTB6WxxfHwmHIvBKp-HLPLw-uzQg", version: "weekly" });
          loader.load().catch(() => {
            // ignore failures here
          });
        }
      } catch (e) {}
    } else {
      setSiteData({
        ...siteData,
        isLoading: true,
      });
    }
  }, [ pageData ]);

  let curPage = siteData?.pages[curLocation];

  const parentPage =
    curPage?.parent?.node ||
    siteData?.pages[parentSlugId] ||
    curPage?.curParent ||
    [];
  const curChildren = parentPage?.children
    ? parentPage.children?.nodes || parentPage.children
    : curPage?.children?.nodes || curPage?.children || [];

  useEffect(() => {
      if (curPage) {
      if (curPage && !curPage?.seo) {
        // missing SEO check
      }
      let newSiteData = { ...siteData };

      const featuredImage =
        curPage?.featuredImage || parentPage?.featuredImage || null;
      if (featuredImage?.mediaItemUrl || featuredImage?.node?.mediaItemUrl)
        newSiteData.curPanelBackground =
          featuredImage.mediaItemUrl || featuredImage?.node?.mediaItemUrl;

      newSiteData.curChildren = curChildren;
      newSiteData.prevPage = null;
      newSiteData.nextPage = curChildren[0]?.node || curChildren[0];

      curChildren.forEach((item, index) => {
        if ((item?.node?.slug || item?.slug) === slugId) {
          const prev = index > 0 ? index - 1 : null;
          const next = index <= curChildren.length ? index + 1 : null;

          if (index === 0) {
            newSiteData.prevPage =
              siteData?.curParent?.node ||
              curPage?.parent?.node ||
              curPage?.parent ||
              null;
          } else {
            newSiteData.prevPage = curChildren[prev]?.node || curChildren[prev];
          }
          newSiteData.nextPage = curChildren[next]?.node || curChildren[next];
        }
      });

      if (curPage?.slug === parentSlugId) {
        newSiteData.prevPage = curPage;
      }

      if (parentPage && !newSiteData.prevPage) {
        newSiteData.prevPage = parentPage;
      }

      setSiteData({
        ...newSiteData,
        curParent: parentPage || curPage,
        prevLocation: siteData.curLocation,
        curLocation: curLocation,
        curLocations: curLocations,
        curSlug: slugId,
        isLoading: false,
      });
    }
  }, [ curPage ]);

  // useEffect(() => {
  //   const hiddenContent = document.getElementById("page-content");
  //   if (hiddenContent) {
  //     setTimeout(() => {
  //       console.log("adding animated to sidenav page1");
  //       hiddenContent.classList.add("animated");
  //     }, 50);
  //   }
  // }, [ curPage, pageData ]);

  if (!siteData) return null;

  return (
    <div id="side-nav-page" className="min-h-screen">
      {curPage && (
        <>
          {curPage?.seo && (
            <MetaHead
              seoObject={curPage}
              addMap={!!curPage?.googleMaps?.latitude}
            />
          )}
          <BackgroundImagePanel url={siteData.curPanelBackground} />
          <main
            id="main-container"
            className="trans-ease min-h-screen w-full z-10 mt-[197px] md:mt-[208px] lg:mt-[224px] relative grid min-[768px]:grid-cols-9 min-[1200px]:grid-cols-12 max-w-[540px] min-[768px]:max-w-[758px] min-[992px]:max-w-[960px] min-[1200px]:max-w-[1140px] min-[1366px]:max-w-[1200px] min-[1600px]:max-w-[1400px] min-[1846px]:max-w-[1600px] m-auto mb-4 gap-[1px]"
          >
            <div className="min-[768px]:col-span-3 side-container hidden min-[768px]:block -mt-[81px]">
              <PageNavigation
                size="full"
                page={curPage}
                parent={siteData.curParent}
                children={siteData.curChildren}
              />
            </div>
            <section className="bg-[#fcfcfc] min-[768px]:col-span-6 min-[1200px]:col-span-9 mx-auto w-full -mt-[81px] blue-arrows">
              <PageContent page={curPage} />
              {(siteData?.prevPage?.title || siteData?.nextPage?.title) && (
                <ChildrenNavigation
                  curPage={curPage}
                  parentPage={parentPage}
                  siteData={siteData}
                />
              )}
            </section>
          </main>
          <div className="pb-4 side-container block min-[768px]:hidden px-2 max-w-[540px] min-[768px]:max-w-[758px] m-auto">
            <PageNavigation
              size="full"
              page={curPage}
              parent={siteData.curParent}
              children={siteData.curChildren}
            />
          </div>
        </>
      )}
    </div>
  );
}
